import { useSelector } from "react-redux";
import { Grid, Row, Col, Text, Button } from "rsuite";
import { selectUsersLicenses, selectUsersLicensesErrorMsg, selectUsersLicensesInProgress, selectUsersLicensesSuccess } from "../../../store/slices/dashboard/dashboardSelectors";
import { selectConfigExpressRegistrationTypeVerbiage, selectConfigExpressRegistrationVerbiage } from "../../../store/slices/config/configSelectors";
import IconIndicator from "../../user-feedback/IconIndicator";
import React, { useMemo } from "react";
import Notice from "../../user-feedback/Notice";
import UsersLicenseRow from "./UserLicenseRow";
import pluralize from "pluralize";
import usePlaceNavigate from "../../../hooks/usePlaceNavigate";
import { C_ACTIVE_PERMIT_STATUSES } from "../../../common/consts";
import { titleize } from "../../../common/utils";

const UsersLicenses = () => {
  const placeNavigate = usePlaceNavigate();

  const usersLicensesInProgress = useSelector(selectUsersLicensesInProgress);
  const usersLicensesSuccess = useSelector(selectUsersLicensesSuccess);
  const usersLicensesErrorMessage = useSelector(selectUsersLicensesErrorMsg);
  const usersLicenses = useSelector(selectUsersLicenses);
  const registrationVerbiage = useSelector(selectConfigExpressRegistrationVerbiage);
  const typeVerbiage = useSelector(selectConfigExpressRegistrationTypeVerbiage);

  const groupAndSortLicenses = (licenses) => {
    const statusOrder = ["current", "submitted"];
    let licensesCopy = [...licenses];

    licensesCopy.sort((a, b) => {
      const statusA = statusOrder.indexOf(a.status);
      const statusB = statusOrder.indexOf(b.status);

      if (statusA !== statusB) return statusA - statusB;
      if (a.status === b.status) return b.application_number.localeCompare(a.application_number);
      return b.status.localeCompare(a.status);
    })
    return licensesCopy;
  }

  const activeLicenses = useMemo(() => {
    return (usersLicenses ?? []).filter(license => C_ACTIVE_PERMIT_STATUSES.includes(license.status));
  }, [usersLicenses]);

  const truncatedActiveLicensesToDisplay = useMemo(() => {
    const sortedLicenses = groupAndSortLicenses(activeLicenses);
    return sortedLicenses?.slice(0, 4);
  }, [activeLicenses]);

  const numAdditionalActivePermitsUndisplayed = useMemo(() => {
    return (activeLicenses.length || 0) - (truncatedActiveLicensesToDisplay?.length || 0);
  }, [usersLicenses, truncatedActiveLicensesToDisplay]);

  const numAdditionalInactivePermitsUndisplayed = useMemo(() => {
    return (usersLicenses.length || 0) - (activeLicenses?.length || 0);
  }, [usersLicenses, activeLicenses]);

  const numAdditionalPermitsUndisplayedText = useMemo(() => {
    let text = '';
    if (activeLicenses.length > 4 && numAdditionalInactivePermitsUndisplayed === 0) {
      text = `+ ${numAdditionalActivePermitsUndisplayed} more Active ${titleize(pluralize(typeVerbiage, numAdditionalActivePermitsUndisplayed))}`;
    }
    else if (activeLicenses.length <= 4 && numAdditionalInactivePermitsUndisplayed > 0) {
      const prefix = activeLicenses.length === 0 ? 'show' : '+';
      text = `${prefix} ${numAdditionalInactivePermitsUndisplayed} Inactive ${titleize(pluralize(typeVerbiage, numAdditionalInactivePermitsUndisplayed))}`;
    }
    else if (numAdditionalInactivePermitsUndisplayed > 0 && activeLicenses.length > 4) {
      text = `+ ${numAdditionalActivePermitsUndisplayed} more Active ${titleize(pluralize(typeVerbiage, numAdditionalActivePermitsUndisplayed))} + ${numAdditionalInactivePermitsUndisplayed} Inactive ${titleize(pluralize(typeVerbiage, numAdditionalInactivePermitsUndisplayed))}`;
    }
    return text;
  }, [activeLicenses, numAdditionalInactivePermitsUndisplayed, numAdditionalActivePermitsUndisplayed, typeVerbiage]);

  if (usersLicensesInProgress) {
    return <IconIndicator />;
  }

  if (usersLicensesSuccess) {
    if (activeLicenses?.length > 0) {
      return (
        <Grid role="grid" fluid className="grdGreyStripe">
          {truncatedActiveLicensesToDisplay.map((license) => {
            return (
              <UsersLicenseRow key={`license_${license.license_else_application_number}`} license={license} />
            )
          })}
          {(numAdditionalActivePermitsUndisplayed > 0 || numAdditionalInactivePermitsUndisplayed > 0) && (
            <Row>
              <Col xs={24}>
                <Button appearance="link" style={{ color: 'unset', textDecoration: 'underline' }} onClick={() => { placeNavigate("/all-permits") }}>
                  {numAdditionalPermitsUndisplayedText}
                </Button>
              </Col>
            </Row>
          )}
        </Grid>
      );
    } else {
      return (
        <Grid fluid className='pv-24'>
          <Row>
            <Col xs={24} className="text-center">
              <Text as='i'>{`You don't have any Active ${pluralize(registrationVerbiage)}.`}</Text>
            </Col>
          </Row>
          {(numAdditionalActivePermitsUndisplayed > 0 || numAdditionalInactivePermitsUndisplayed > 0) && (
            <Row className="text-center">
              <Col xs={24}>
                <Button appearance="link" style={{ color: 'unset', textDecoration: 'underline' }} onClick={() => { placeNavigate("/all-permits") }}>
                  {numAdditionalPermitsUndisplayedText}
                </Button>
              </Col>
            </Row>
          )}
        </Grid>
      )
    }
  }

  return <Notice message={usersLicensesErrorMessage} style={{ width: "100%" }} />;
};


export default UsersLicenses;
